'use strict';

import {DOM} from "../helpers/DOM.js";
import {Helper} from "../helpers/Helper.js";
import {Toast} from "../helpers/Toast.js";

export class MyAccount {
	#myAccountLoading = false;
	constructor() {
		this.#setLayoutVars();
		this.addEvents();
		this.#applyMobileSidebarState();
		this.donors();
		this.exportDonors();
		this.donorDonationsModal();
		this.allDonationsModal();
		this.campaignDonationsModal();
		this.editAccount();
		this.cardDetailsModal();
		this.#myAccountLoading = true;
	}

	/** @returns {boolean} */
	#isMobile() {
		return window.matchMedia('(max-width: 767px)').matches;
	}

	getDonorsDatatableApi() {
		return window.LevDataTables?.datatableInstances?.['lev-charity-my-account-donors-datatable'] || null;
	}

	decodeHtmlEntities(value) {
		const textarea = document.createElement('textarea');
		textarea.innerHTML = value ?? '';
		return textarea.value;
	}

	parseDonorFromCheckbox(checkboxOrValue) {
		if (checkboxOrValue && typeof checkboxOrValue === 'object' && checkboxOrValue.dataset?.email) {
			return {
				id: checkboxOrValue.dataset.donorId || '',
				email: checkboxOrValue.dataset.email,
			};
		}

		const rawValue = typeof checkboxOrValue === 'string'
			? checkboxOrValue
			: (checkboxOrValue?.value || '');

		if (!rawValue || rawValue === 'all') {
			return null;
		}

		try {
			return JSON.parse(rawValue);
		} catch (_) {
			try {
				return JSON.parse(this.decodeHtmlEntities(rawValue));
			} catch (__) {
				return null;
			}
		}
	}

	/**
	 * All donor row checkboxes across DataTables pages (not only the current page DOM).
	 * @returns {HTMLInputElement[]}
	 */
	getDonorRowCheckboxes() {
		const api = this.getDonorsDatatableApi();
		if (api) {
			return api.rows({ search: 'applied' }).nodes().toArray()
				.map((row) => row.querySelector('input.send_email_to_user'))
				.filter(Boolean);
		}

		return [...document.querySelectorAll('#lev-charity-my-account-donors-datatable tbody input.send_email_to_user')]
			.filter((checkbox) => checkbox.value !== 'all');
	}

	getSelectedDonorsFromDatatable() {
		const allHeader = document.querySelector(
			'#lev-charity-my-account-donors-datatable thead input.send_email_to_user[value="all"]'
		);

		// Header "select all" means every filtered donor across pages, not only the current page DOM.
		if (allHeader?.checked) {
			return this.getAllDonorsFromDatatableRows();
		}

		const donors = [];
		const seenEmails = new Set();

		this.getDonorRowCheckboxes()
			.filter((checkbox) => checkbox.checked)
			.forEach((checkbox) => {
				const donor = this.parseDonorFromCheckbox(checkbox);
				const email = donor?.email?.trim();
				const key = email?.toLowerCase();
				if (!email || !key || seenEmails.has(key)) {
					return;
				}
				seenEmails.add(key);
				donors.push({
					id: donor.id || '',
					email,
				});
			});

		return donors;
	}

	getAllDonorsFromDatatableRows() {
		const donors = [];
		const seenEmails = new Set();
		const api = this.getDonorsDatatableApi();

		if (api) {
			api.rows({ search: 'applied' }).data().toArray().forEach((row) => {
				const cells = Array.isArray(row) ? row : Object.values(row);
				const checkboxHtml = String(cells[0] || '');
				const emailAttrMatch = checkboxHtml.match(/data-email=(?:'([^']+)'|"([^"]+)")/i);
				const emailFromAttr = this.decodeHtmlEntities(emailAttrMatch ? (emailAttrMatch[1] || emailAttrMatch[2] || '') : '');
				const valueMatch = checkboxHtml.match(/value=(?:'([^']+)'|"([^"]+)")/);
				const rawValue = valueMatch ? (valueMatch[1] || valueMatch[2] || '') : '';
				const donor = this.parseDonorFromCheckbox(this.decodeHtmlEntities(rawValue))
					|| (emailFromAttr ? { id: '', email: emailFromAttr } : null);
				const email = donor?.email?.trim();
				const key = email?.toLowerCase();

				if (!email || !key || seenEmails.has(key)) {
					return;
				}

				seenEmails.add(key);
				donors.push({
					id: donor.id || '',
					email,
				});
			});

			if (donors.length) {
				return donors;
			}
		}

		this.getDonorRowCheckboxes().forEach((checkbox) => {
			const donor = this.parseDonorFromCheckbox(checkbox);
			const email = donor?.email?.trim();
			const key = email?.toLowerCase();
			if (!email || !key || seenEmails.has(key)) {
				return;
			}
			seenEmails.add(key);
			donors.push({
				id: donor.id || '',
				email,
			});
		});

		return donors;
	}

	#applyMobileSidebarState() {
		const sidebar = document.querySelector('.levcharity_my_account_sidebar');
		if (!sidebar) return;
		if (this.#isMobile()) {
			sidebar.classList.add('closed');
			Helper.setCookie('levcharity_my_account_sidebar_state', 'closed', 365);
		}
	}

	#portalMyAccountToBody(el) {
		if (!document.body.classList.contains('levcharity-my-account--with-theme-header')) {
			return;
		}
		if (el.parentNode === document.body) {
			document.body.classList.add('levcharity-my-account-portal-ready');
			return;
		}
		document.body.classList.add('levcharity-my-account-portal-ready');
		document.body.appendChild(el);
	}

	/**
	 * The portal is an opaque fixed layer starting at the header's bottom edge, so the header has to
	 * paint above it or its nav dropdowns end up hidden behind it. z-index only takes effect on a
	 * positioned element, so themes that leave the header static get position:relative as well.
	 */
	#liftThemeHeaderAbovePortal(themeHeader) {
		if (!themeHeader) return;

		const staticClass = 'levcharity-my-account-theme-header--static';

		if (
			!themeHeader.classList.contains(staticClass) &&
			window.getComputedStyle(themeHeader).position === 'static'
		) {
			themeHeader.classList.add(staticClass);
		}

		themeHeader.classList.add('levcharity-my-account-theme-header');
	}

	#setLayoutVars() {
		const el = document.getElementById('levcharity_my_account');
		if (!el) return;

		this.#portalMyAccountToBody(el);

		const update = () => {
			if (document.body.classList.contains('levcharity-my-account--with-theme-header')) {
				this.#portalMyAccountToBody(el);
				const themeHeader = document.querySelector(
					'#levcharity-w4f-site-header, .wp-site-blocks > header, header.wp-block-template-part'
				);
				const headerHeight = themeHeader
					? Math.ceil(themeHeader.getBoundingClientRect().height)
					: 0;

				this.#liftThemeHeaderAbovePortal(themeHeader);

				document.documentElement.style.setProperty(
					'--levcharity-theme-header-height',
					`${headerHeight}px`
				);
				el.style.removeProperty('--lc-header-height');
			} else {
				document.documentElement.style.removeProperty('--levcharity-theme-header-height');
				const top = el.getBoundingClientRect().top + window.scrollY;
				el.style.setProperty('--lc-header-height', `${top}px`);
			}

			this.#applyMobileSidebarState();
		};

		update();
		window.addEventListener('resize', update);
		window.addEventListener('load', update);
	}

	cardDetailsModal() {
		DOM.addEvent('click', '.js-view-card-details', (e, button) => {
			e.preventDefault();
			const last4 = button.dataset.cardLast4 || '';
			const type = button.dataset.cardType || '';
			const expiry = button.dataset.cardExpiry || '';
			const typeName = type.charAt(0).toUpperCase() + type.slice(1);

			const existing = document.querySelector('.lc-card-details-modal');
			if (existing) existing.remove();

			const modal = document.createElement('div');
			modal.className = 'lc-card-details-modal';
			modal.innerHTML = `
				<div class="lc-card-details-modal__overlay"></div>
				<div class="lc-card-details-modal__content">
					<button class="lc-card-details-modal__close" aria-label="Close">&times;</button>
					<h3>${typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Card Details', 'levcharity') : 'Card Details'}</h3>
					<div class="lc-card-details-modal__info">
						<div class="lc-card-details-modal__row">
							<span class="lc-card-details-modal__label">${typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Card Type', 'levcharity') : 'Card Type'}</span>
							<span class="lc-card-details-modal__value">${typeName}</span>
						</div>
						<div class="lc-card-details-modal__row">
							<span class="lc-card-details-modal__label">${typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Card Number', 'levcharity') : 'Card Number'}</span>
							<span class="lc-card-details-modal__value">•••• •••• •••• ${last4}</span>
						</div>
						${expiry ? `<div class="lc-card-details-modal__row">
							<span class="lc-card-details-modal__label">${typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Expires', 'levcharity') : 'Expires'}</span>
							<span class="lc-card-details-modal__value">${expiry}</span>
						</div>` : ''}
					</div>
				</div>
			`;
			document.body.appendChild(modal);
			requestAnimationFrame(() => modal.classList.add('active'));

			const close = () => {
				modal.classList.remove('active');
				setTimeout(() => modal.remove(), 200);
			};
			modal.querySelector('.lc-card-details-modal__close').addEventListener('click', close);
			modal.querySelector('.lc-card-details-modal__overlay').addEventListener('click', close);
		});
	}

	addEvents() {
		DOM.addEvent('click', '.levcharity_my_account_menu_item--expand', (e, button) => {
			e.preventDefault();
			if (this.#isMobile()) return;
			const sidebar = button.closest('aside');

			sidebar.classList.remove('closed');
			Helper.setCookie('levcharity_my_account_sidebar_state', 'open', 365);
		});
		DOM.addEvent('click', '.levcharity_my_account_menu_item--collapse', (e, button) => {
			e.preventDefault()
			const sidebar = button.closest('aside');

			sidebar.classList.add('closed')
			Helper.setCookie('levcharity_my_account_sidebar_state', 'closed', 365)
		})
		DOM.addEvent('click', '.submenu-indicator', (e, button) => {
			e.preventDefault()

			const parent = button.closest('li');
			parent.classList.toggle('levcharity_my_account_menu_item--closed')
			if (!parent.classList.contains('levcharity_my_account_menu_item--closed')) {
				Helper.setCookie('levcharity_my_account_expanded_' + parent.id, 'true', 365)
			} else {
				Helper.setCookie('levcharity_my_account_expanded_' + parent.id, '', -1)
			}
		})
	}


	async donors() {
		const hiddenInputId = 'selected_donors';
		const selectedEmailsButton = document.querySelector('.selected-emails');
		const allSelectedEmails = document.querySelector('.all-selected-emails');

		const emailsModal = document.querySelector('#emailsModal');
		// all-selected-emails is optional in markup; do not bail out if it is missing
		if (!emailsModal || !selectedEmailsButton) {
			return;
		}

		const closeModal = document.querySelector('.close_modal');
		const closeModalButton = document.querySelector('.close_modal_button');
		const recipientLabel = emailsModal.querySelector('.recipients-label');
		const form = emailsModal.querySelector('form');
		if (!form || !recipientLabel) {
			return;
		}

		const donorsEmailEditorId = 'donors_email_message';
		const sendButton = form.querySelector('.send_emails_button');
		const emailFromInput = form.dataset?.emailFrom || '';
		const emailSubjectInput = form.querySelector('input[name="email_subject"]');
		const emailMessageInput = form.querySelector('textarea[name="email_message"]')
			|| document.getElementById(donorsEmailEditorId);

		const getDonorsEmailMessage = () => {
			if (typeof window.tinymce !== 'undefined') {
				const editor = window.tinymce.get(donorsEmailEditorId);
				if (editor) {
					editor.save();
					if (!editor.isHidden()) {
						return editor.getContent();
					}
				}
			}
			return emailMessageInput?.value || '';
		};

		const resetDonorsEmailMessage = () => {
			if (typeof window.tinymce !== 'undefined') {
				const editor = window.tinymce.get(donorsEmailEditorId);
				if (editor && !editor.isHidden()) {
					editor.setContent('');
					editor.fire('change');
					return;
				}
			}
			if (emailMessageInput) {
				emailMessageInput.value = '';
			}
		};

		let hiddenInput = document.getElementById(hiddenInputId);

		// Create hidden input if not exists
		if (!hiddenInput) {
			hiddenInput = document.createElement('input');
			hiddenInput.type = 'hidden';
			hiddenInput.name = 'selected_donors';
			hiddenInput.id = hiddenInputId;
			document.body.appendChild(hiddenInput);
		}

		const recipientsPickerSection = emailsModal.querySelector('.recipients-picker-section');
		const recipientsPicker = emailsModal.querySelector('#donors_recipients_picker');
		if (!recipientsPickerSection || !recipientsPicker) {
			return;
		}

		let donorsChoicesInstance = null;

		// 'all' | 'selected' – set when opening modal so single send handler knows what to send
		let sendMode = 'selected';

		function getRecipients() {
			try {
				if (!hiddenInput.value) return [];

				// Try to parse as array first (new format)
				const parsed = JSON.parse('[' + hiddenInput.value + ']');
				return Array.isArray(parsed) ? parsed : [];
			} catch (e) {
				return [];
			}
		}

		function setRecipients(recipients) {
			hiddenInput.value = recipients.map(r => JSON.stringify(r)).join(',');
		}

		function getAdditionalEmails() {
			return getRecipients()
				.filter((recipient) => !recipient?.id && recipient?.email)
				.map((recipient) => recipient.email);
		}

		const escapeHtml = (value) => {
			const element = document.createElement('div');
			element.textContent = value ?? '';
			return element.innerHTML;
		};

		const getTableSelectedDonors = () => this.getSelectedDonorsFromDatatable();

		const getAllDonorsFromDatatable = () => this.getAllDonorsFromDatatableRows();

		const onRecipientsPickerChange = () => {
			syncPickerRecipients();
		};

		const destroyRecipientsPicker = () => {
			recipientsPicker.removeEventListener('change', onRecipientsPickerChange);

			if (donorsChoicesInstance) {
				donorsChoicesInstance.destroy();
				donorsChoicesInstance = null;
			}

			recipientsPicker.innerHTML = '';
		};

		const syncPickerRecipients = () => {
			if (!donorsChoicesInstance) {
				return;
			}

			const selectedValues = donorsChoicesInstance.getValue(true);
			const recipients = selectedValues.map((email) => {
				const option = [...recipientsPicker.options].find((item) => item.value === email);
				return {
					id: option?.dataset?.donorId || '',
					email,
				};
			}).filter((recipient) => recipient.email);

			setRecipients(recipients);
			updateSelectionCount(recipients.length);
		};

		const initRecipientsPicker = async (allDonors, preselected = []) => {
			destroyRecipientsPicker();
			recipientsPicker.removeEventListener('change', onRecipientsPickerChange);

			const preselectedEmails = new Set(
				preselected.map((donor) => donor.email?.toLowerCase()).filter(Boolean)
			);

			recipientsPicker.innerHTML = allDonors.map((donor) => {
				const email = donor.email || '';
				const isSelected = preselectedEmails.has(email.toLowerCase()) ? ' selected' : '';

				return `<option value="${escapeHtml(email)}" data-donor-id="${escapeHtml(String(donor.id || ''))}"${isSelected}>${escapeHtml(email)}</option>`;
			}).join('');

			const { default: Choices } = await import(/* webpackChunkName: "choices" */ 'choices.js');
			const placeholder = recipientsPicker.dataset.placeholder
				|| (typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Select donors…', 'levcharity') : 'Select donors…');

			donorsChoicesInstance = new Choices(recipientsPicker, {
				removeItemButton: true,
				shouldSortItems: false,
				itemSelectText: '',
				placeholderValue: placeholder,
				searchPlaceholderValue: typeof wp !== 'undefined' && wp.i18n
					? wp.i18n.__('Search donors…', 'levcharity')
					: 'Search donors…',
			});

			recipientsPicker.addEventListener('change', onRecipientsPickerChange);
			syncPickerRecipients();
		};

		const openRecipientsPicker = async (preselected = []) => {
			await initRecipientsPicker(getAllDonorsFromDatatable(), preselected);
		};

		function reloadDonorsDatatable() {
			const tableId = 'lev-charity-my-account-donors-datatable';
			const dtInstance = window.LevDataTables?.datatableInstances?.[tableId];
			if (dtInstance) {
				dtInstance.ajax.reload(null, false);
			}
		}

		function updateSelectionCount(count = getRecipients().length) {
			recipientLabel.textContent = `Recipients (${count})`;
		}

		const additionalEmailInput = form.querySelector('input[name="additional_email"]');
		const additionalEmailButton = form.querySelector('.additional_email_button button');
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		const normalizeAdditionalEmailsInput = (value) => (
			value
				.replace(/\s+/g, ',')
				.replace(/,+/g, ',')
				.replace(/^,+/g, '')
		);

		const parseAdditionalEmails = (value) => (
			normalizeAdditionalEmailsInput(value)
				.split(',')
				.map((email) => email.trim())
				.filter(Boolean)
		);

		const addAdditionalEmailToPicker = (email) => {
			const normalizedEmail = email.trim().toLowerCase();
			if (!normalizedEmail) {
				return false;
			}

			if (getRecipients().some((recipient) => recipient.email?.toLowerCase() === normalizedEmail)) {
				return false;
			}

			if (!donorsChoicesInstance) {
				setRecipients([...getRecipients(), { id: '', email }]);
				updateSelectionCount(getRecipients().length);
				return true;
			}

			const currentValues = donorsChoicesInstance.getValue(true);
			const hasOption = [...recipientsPicker.options].some(
				(option) => option.value.toLowerCase() === normalizedEmail
			);

			if (!hasOption) {
				donorsChoicesInstance.setChoices(
					[{ value: email, label: email, selected: true }],
					'value',
					'label',
					false
				);
			} else {
				donorsChoicesInstance.setValue([...currentValues, email]);
			}

			syncPickerRecipients();
			return true;
		};

		if (additionalEmailInput && additionalEmailButton) {
			additionalEmailInput.addEventListener('input', (event) => {
				const normalizedValue = normalizeAdditionalEmailsInput(event.target.value);
				if (event.target.value !== normalizedValue) {
					event.target.value = normalizedValue;
				}

				const hasContent = parseAdditionalEmails(normalizedValue).length > 0
					|| normalizedValue.replace(/,/g, '').length > 0;
				additionalEmailButton.style.opacity = hasContent ? '1' : '0.5';
				additionalEmailButton.disabled = !hasContent;
			});

			additionalEmailButton.style.opacity = '0.5';
			additionalEmailButton.disabled = true;

			additionalEmailButton.addEventListener('click', (event) => {
				event.preventDefault();
				const emails = parseAdditionalEmails(additionalEmailInput.value);

				if (!emails.length) {
					Toast.error(typeof wp !== 'undefined' && wp.i18n
						? wp.i18n.__('Please enter at least one email address.', 'levcharity')
						: 'Please enter at least one email address.');
					return;
				}

				const invalidEmails = [];
				const duplicateEmails = [];
				let addedCount = 0;

				emails.forEach((email) => {
					if (!emailRegex.test(email)) {
						invalidEmails.push(email);
						return;
					}

					if (addAdditionalEmailToPicker(email)) {
						addedCount++;
						return;
					}

					duplicateEmails.push(email);
				});

				if (!addedCount) {
					if (invalidEmails.length) {
						Toast.error(typeof wp !== 'undefined' && wp.i18n
							? wp.i18n.__('Please enter valid email addresses.', 'levcharity')
							: 'Please enter valid email addresses.');
					} else {
						Toast.error(typeof wp !== 'undefined' && wp.i18n
							? wp.i18n.__('These email addresses are already in the list.', 'levcharity')
							: 'These email addresses are already in the list.');
					}
					return;
				}

				if (invalidEmails.length || duplicateEmails.length) {
					const skippedCount = invalidEmails.length + duplicateEmails.length;
					Toast.success(typeof wp !== 'undefined' && wp.i18n
						? wp.i18n.sprintf(
							wp.i18n.__('%1$d added. %2$d skipped (invalid or already added).', 'levcharity'),
							addedCount,
							skippedCount
						)
						: `${addedCount} added. ${skippedCount} skipped (invalid or already added).`);
				}

				additionalEmailInput.value = '';
				additionalEmailButton.style.opacity = '0.5';
				additionalEmailButton.disabled = true;
			});
		}

		document.addEventListener('change', (e) => {
			const target = e.target;

			if (target && target.matches('input.send_email_to_user')) {
				const rowCheckboxes = this.getDonorRowCheckboxes();
				const allBox = document.querySelector(
					'#lev-charity-my-account-donors-datatable thead input.send_email_to_user[value="all"]'
				) || document.querySelector('input.send_email_to_user[value="all"]');

				if (target.value === 'all' && target.checked) {
					rowCheckboxes.forEach((checkbox) => {
						checkbox.checked = true;
					});
					if (allBox) {
						allBox.checked = true;
					}
				} else if (target.value === 'all' && !target.checked) {
					rowCheckboxes.forEach((checkbox) => {
						checkbox.checked = false;
					});
					if (allBox) {
						allBox.checked = false;
					}
				} else {
					const selectedCount = rowCheckboxes.filter((checkbox) => checkbox.checked).length;
					if (allBox) {
						allBox.checked = rowCheckboxes.length > 0 && selectedCount === rowCheckboxes.length;
					}
				}
			}
		});

		// Keep row checkboxes checked when paging while header "select all" is active.
		const syncSelectAllOnDraw = () => {
			const allBox = document.querySelector(
				'#lev-charity-my-account-donors-datatable thead input.send_email_to_user[value="all"]'
			);
			if (!allBox?.checked) {
				return;
			}
			this.getDonorRowCheckboxes().forEach((checkbox) => {
				checkbox.checked = true;
			});
		};

		const bindSelectAllDrawSync = () => {
			const api = this.getDonorsDatatableApi();
			if (!api?.on) {
				return false;
			}
			api.off('draw.dt', syncSelectAllOnDraw);
			api.on('draw.dt', syncSelectAllOnDraw);
			return true;
		};

		if (!bindSelectAllDrawSync()) {
			let attempts = 0;
			const timer = setInterval(() => {
				attempts += 1;
				if (bindSelectAllDrawSync() || attempts > 40) {
					clearInterval(timer);
				}
			}, 250);
		}

		function closeEmailModal() {
			emailsModal.classList.remove('active');
			destroyRecipientsPicker();
		}

		closeModal?.addEventListener('click', closeEmailModal);
		closeModalButton?.addEventListener('click', closeEmailModal);

		selectedEmailsButton.addEventListener('click', async () => {
			sendMode = 'selected';
			await openRecipientsPicker(getTableSelectedDonors());
			emailsModal.classList.add('active');
		});

		if (allSelectedEmails) {
			allSelectedEmails.addEventListener('click', async () => {
				sendMode = 'selected';
				await openRecipientsPicker(getAllDonorsFromDatatable());
				emailsModal.classList.add('active');
			});
		}

		if (!sendButton || !emailSubjectInput || !emailMessageInput) {
			return;
		}

		// Store original button content once so we can restore after loading state
		if (!sendButton.dataset.originalHtml) {
			sendButton.dataset.originalHtml = sendButton.innerHTML;
		}

		function setSendButtonLoading(loading) {
			sendButton.disabled = loading;
			sendButton.classList.toggle('is-loading', loading);
			const sendingText = typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Sending…', 'levcharity') : 'Sending…';
			if (loading) {
				sendButton.innerHTML = `<span class="send-button-loading-text">${sendingText}</span>`;
			} else if (sendButton.dataset.originalHtml) {
				sendButton.innerHTML = sendButton.dataset.originalHtml;
			}
		}

		const resetForm = () => {
			emailSubjectInput.value = '';
			resetDonorsEmailMessage();
			if (additionalEmailInput) {
				additionalEmailInput.value = '';
			}
			if (additionalEmailButton) {
				additionalEmailButton.style.opacity = '0.5';
				additionalEmailButton.disabled = true;
			}
			setRecipients([]);
			sendMode = 'selected';
			destroyRecipientsPicker();
			updateSelectionCount(0);

			const allCheckboxes = [
				...this.getDonorRowCheckboxes(),
				document.querySelector('#lev-charity-my-account-donors-datatable thead input.send_email_to_user[value="all"]'),
			].filter(Boolean);
			allCheckboxes.forEach((checkbox) => {
				checkbox.checked = false;
			});

			sendButton.classList.remove('is-loading');
			sendButton.disabled = false;
			if (sendButton.dataset.originalHtml) {
				sendButton.innerHTML = sendButton.dataset.originalHtml;
			}
		};

		const defaultSuccessMessage = typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Emails sent successfully.', 'levcharity') : 'Emails sent successfully.';
		const defaultErrorMessage = typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Something went wrong. Please try again.', 'levcharity') : 'Something went wrong. Please try again.';

		// Single send handler (attached once) – uses sendMode set when opening modal
		sendButton.addEventListener('click', (e) => {
			e.preventDefault();
			const subject = emailSubjectInput.value.trim();
			const message = getDonorsEmailMessage().trim();

			if (!subject) {
				Toast.error(typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Please enter a subject.', 'levcharity') : 'Please enter a subject.');
				emailSubjectInput.focus();
				return;
			}
			if (!message) {
				Toast.error(typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Please enter a message.', 'levcharity') : 'Please enter a message.');
				emailMessageInput.focus();
				return;
			}

			const donorsPayload = sendMode === 'all' ? 'all' : (document.getElementById(hiddenInputId)?.value || '');
			if (sendMode === 'selected' && !donorsPayload) {
				Toast.error(typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__('Please select at least one recipient.', 'levcharity') : 'Please select at least one recipient.');
				return;
			}

			setSendButtonLoading(true);

			Helper.request(admin_ajax[0], {
				action: form.dataset.action,
				token: form.dataset.token,
				from: form.dataset.emailFrom || emailFromInput,
				subject,
				message,
				donors: donorsPayload,
				additional_emails: getAdditionalEmails()
			})
				.then((response) => {
					setSendButtonLoading(false);
					if (response.success) {
						resetForm();
						reloadDonorsDatatable();
						closeEmailModal();
						Toast.success(response.data?.message || defaultSuccessMessage);
					} else {
						Toast.error(response.data?.message || response.message || defaultErrorMessage);
					}
				})
				.catch((error) => {
					setSendButtonLoading(false);
					Toast.error(error?.message || defaultErrorMessage);
				});
		});
	}

	donorDonationsModal() {
		document.addEventListener('click', (e) => {
			const button = e.target.closest('.actions-option[data-donor-email]');
			if (!button) return;

			e.preventDefault();
			e.stopPropagation();

			const email = button.dataset.donorEmail || '';
			const name = button.dataset.donorName || '';
			if (!email) return;

			const existing = document.querySelector('.lc-donor-donations-modal');
			if (existing) existing.remove();

			const tableId = 'lc-donor-donations-table-' + Date.now();
			const queryData = JSON.stringify({ email });

			const i18n = (fallback) => (typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__(fallback, 'levcharity') : fallback);
			const escHtml = (str) => {
				const d = document.createElement('div');
				d.textContent = str;
				return d.innerHTML;
			};

			const modal = document.createElement('div');
			modal.className = 'lc-donor-donations-modal active';
			modal.innerHTML = `
				<div class="lc-donor-donations-modal__overlay"></div>
				<div class="lc-donor-donations-modal__content">
					<div class="lc-donor-donations-modal__header">
						<h3>${i18n('Donations')}</h3>
						<button class="lc-donor-donations-modal__close" aria-label="Close">&times;</button>
					</div>
					<div class="lc-donor-donations-modal__body">
						<div class="datatable_wrapper">
							<table id="${tableId}"
								class="dataTable display"
								data-action="my_account_user_donor_donations"
								data-query='${queryData.replace(/'/g, '&#39;')}'
								data-nosorting="4"
							>
								<thead>
									<tr>
										<th>${i18n('Campaign')}</th>
										<th>${i18n('Type')}</th>
										<th>${i18n('Amount')}</th>
										<th>${i18n('Date')}</th>
										<th>${i18n('Status')}</th>
									</tr>
								</thead>
							</table>
						</div>
					</div>
				</div>
			`;

			document.body.appendChild(modal);

			try {
				const tableEl = document.getElementById(tableId);
				if (tableEl && window.LevDataTables) {
					new window.LevDataTables(tableEl);
				}
			} catch (err) {
				console.error('Donor donations datatable init error:', err);
			}

			const close = () => {
				modal.classList.remove('active');
				setTimeout(() => {
					try {
						const dtInstance = window.LevDataTables?.datatableInstances?.[tableId];
						if (dtInstance) {
							dtInstance.destroy();
							delete window.LevDataTables.datatableInstances[tableId];
						}
					} catch (_) {}
					modal.remove();
				}, 250);
			};

			modal.querySelector('.lc-donor-donations-modal__close').addEventListener('click', close);
			modal.querySelector('.lc-donor-donations-modal__overlay').addEventListener('click', close);
		});
	}

	campaignDonationsModal() {
		document.addEventListener('click', (e) => {
			const link = e.target.closest('.levcharity-myaccount-campaigns-container .campaign_donations_count');
			if (!link) return;

			e.preventDefault();

			const campaignType = link.dataset.campaignType || '';
			const campaignName = link.dataset.campaignName || '';
			let dataAction = '';
			let queryParams = {};

			if (campaignType === 'p2p') {
				const p2pCampaignId = parseInt(link.dataset.p2pCampaignId, 10);
				if (!p2pCampaignId) return;
				dataAction = 'my_account_p2p_campaign_donors_datatable';
				queryParams = { p2p_campaign_id: p2pCampaignId };
			} else if (campaignType === 'team') {
				const metaId = parseInt(link.dataset.metaId, 10);
				if (!metaId) return;
				dataAction = 'my_account_team_campaign_donors_datatable';
				queryParams = { meta_id: metaId };
			} else {
				return;
			}

			const existing = document.querySelector('.lc-donor-donations-modal');
			if (existing) existing.remove();

			const tableId = 'lc-campaign-donations-table-' + Date.now();
			const queryData = JSON.stringify(queryParams);
			const i18n = (fallback) => (typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__(fallback, 'levcharity') : fallback);
			const escHtml = (str) => {
				const d = document.createElement('div');
				d.textContent = str;
				return d.innerHTML;
			};
			const modalTitle = campaignName
				? `${i18n('Donations for')}: ${escHtml(campaignName)}`
				: i18n('Donations');

			const modal = document.createElement('div');
			modal.className = 'lc-donor-donations-modal active';
			modal.innerHTML = `
				<div class="lc-donor-donations-modal__overlay"></div>
				<div class="lc-donor-donations-modal__content">
					<div class="lc-donor-donations-modal__header">
						<h3>${modalTitle}</h3>
						<button class="lc-donor-donations-modal__close" aria-label="Close">&times;</button>
					</div>
					<div class="lc-donor-donations-modal__body">
						<div class="datatable_wrapper">
							<table id="${tableId}"
								class="dataTable display"
								data-action="${dataAction}"
								data-query='${queryData.replace(/'/g, '&#39;')}'
								data-order='[[ 1, "desc" ]]'
							>
								<thead>
									<tr>
										<th>${i18n('Donor Name')}</th>
										<th>${i18n('Donation Date')}</th>
										<th>${i18n('Currency')}</th>
										<th>${i18n('Email')}</th>
										<th>${i18n('Total Amount')}</th>
									</tr>
								</thead>
							</table>
						</div>
					</div>
				</div>
			`;

			document.body.appendChild(modal);

			try {
				const tableEl = document.getElementById(tableId);
				if (tableEl && window.LevDataTables) {
					new window.LevDataTables(tableEl);
				}
			} catch (err) {
				console.error('Campaign donations datatable init error:', err);
			}

			const close = () => {
				modal.classList.remove('active');
				setTimeout(() => {
					try {
						const dtInstance = window.LevDataTables?.datatableInstances?.[tableId];
						if (dtInstance) {
							dtInstance.destroy();
							delete window.LevDataTables.datatableInstances[tableId];
						}
					} catch (_) {}
					modal.remove();
				}, 250);
			};

			modal.querySelector('.lc-donor-donations-modal__close').addEventListener('click', close);
			modal.querySelector('.lc-donor-donations-modal__overlay').addEventListener('click', close);
		});
	}

	allDonationsModal() {
		const btn = document.querySelector('.js-all-donations-btn');
		if (!btn) return;

		btn.addEventListener('click', (e) => {
			e.preventDefault();

			const existing = document.querySelector('.lc-donor-donations-modal');
			if (existing) existing.remove();

			const tableId = 'lc-all-donations-table-' + Date.now();
			const i18n = (fallback) => (typeof wp !== 'undefined' && wp.i18n ? wp.i18n.__(fallback, 'levcharity') : fallback);

			const modal = document.createElement('div');
			modal.className = 'lc-donor-donations-modal active';
			modal.innerHTML = `
				<div class="lc-donor-donations-modal__overlay"></div>
				<div class="lc-donor-donations-modal__content">
					<div class="lc-donor-donations-modal__header">
						<h3>${i18n('All Donations')}</h3>
						<button class="lc-donor-donations-modal__close" aria-label="Close">&times;</button>
					</div>
					<div class="lc-donor-donations-modal__body">
						<div class="datatable_wrapper">
							<table id="${tableId}"
								class="dataTable display"
								data-action="my_account_user_all_donations"
								data-nosorting="7"
							>
								<thead>
									<tr>
										<th>${i18n('Donor')}</th>
										<th>${i18n('Email')}</th>
										<th>${i18n('Address')}</th>
										<th>${i18n('Campaign')}</th>
										<th>${i18n('Type')}</th>
										<th>${i18n('Amount')}</th>
										<th>${i18n('Date')}</th>
										<th>${i18n('Status')}</th>
									</tr>
								</thead>
							</table>
						</div>
					</div>
				</div>
			`;

			document.body.appendChild(modal);

			try {
				const tableEl = document.getElementById(tableId);
				if (tableEl && window.LevDataTables) {
					new window.LevDataTables(tableEl);
				}
			} catch (err) {
				console.error('All donations datatable init error:', err);
			}

			const close = () => {
				modal.classList.remove('active');
				setTimeout(() => {
					try {
						const dtInstance = window.LevDataTables?.datatableInstances?.[tableId];
						if (dtInstance) {
							dtInstance.destroy();
							delete window.LevDataTables.datatableInstances[tableId];
						}
					} catch (_) {}
					modal.remove();
				}, 250);
			};

			modal.querySelector('.lc-donor-donations-modal__close').addEventListener('click', close);
			modal.querySelector('.lc-donor-donations-modal__overlay').addEventListener('click', close);
		});
	}

	exportDonors() {
		const exportButton = document.querySelector('.js-export-donors');
		if (!exportButton) return;

		const getSelectedDonorEmails = () => {
			const donors = this.getSelectedDonorsFromDatatable();

			const emails = [];
			const seen = new Set();
			donors.forEach((donor) => {
				const email = donor?.email?.trim();
				const key = email?.toLowerCase();
				if (!email || !key || seen.has(key)) {
					return;
				}
				seen.add(key);
				emails.push(email);
			});

			return emails;
		};

		exportButton.addEventListener('click', (e) => {
			e.preventDefault();

			const action = exportButton.dataset.action;
			const token = exportButton.dataset.token;
			if (!action || !token) return;

			const selectedDonorEmails = getSelectedDonorEmails();
			if (!selectedDonorEmails.length) {
				Toast.error(typeof wp !== 'undefined' && wp.i18n
					? wp.i18n.__('Please select at least one donor to export.', 'levcharity')
					: 'Please select at least one donor to export.');
				return;
			}

			const originalText = exportButton.textContent;
			exportButton.textContent = typeof wp !== 'undefined' && wp.i18n
				? wp.i18n.__('Exporting…', 'levcharity')
				: 'Exporting…';
			exportButton.disabled = true;

			Helper.request(admin_ajax[0], {
				action,
				token,
				donors: JSON.stringify(selectedDonorEmails),
			})
				.then((response) => {
					exportButton.textContent = originalText;
					exportButton.disabled = false;

					if (response.success && response.download_url) {
						const a = document.createElement('a');
						a.href = response.download_url;
						a.download = '';
						document.body.appendChild(a);
						a.click();
						a.remove();
						Toast.success(typeof wp !== 'undefined' && wp.i18n
							? wp.i18n.__('Export downloaded successfully.', 'levcharity')
							: 'Export downloaded successfully.');
					} else {
						Toast.error(response.message || 'Export failed.');
					}
				})
				.catch((error) => {
					exportButton.textContent = originalText;
					exportButton.disabled = false;
					Toast.error(error?.message || 'Export failed.');
				});
		});
	}

	async editAccount(){
		const saveChangesButton = DOM.el('.save_account_button')
		if (!saveChangesButton) return;

		const form = document.querySelector('.edit-account-form');
		if (form) {
			saveChangesButton.disabled = true;

			const fields = form.querySelectorAll('input, select, textarea');
			const initialValues = new Map();
			fields.forEach(field => {
				const key = field.name || field.id;
				if (!key) return;
				if (field.type === 'checkbox') {
					initialValues.set(key, field.checked);
				} else {
					initialValues.set(key, field.value);
				}
			});

			const snapshotValues = () => {
				initialValues.clear();
				fields.forEach(field => {
					const key = field.name || field.id;
					if (!key) return;
					initialValues.set(key, field.type === 'checkbox' ? field.checked : field.value);
				});
			};

			const checkChanges = () => {
				let changed = false;
				fields.forEach(field => {
					const key = field.name || field.id;
					if (!key || !initialValues.has(key)) return;
					const initial = initialValues.get(key);
					const current = field.type === 'checkbox' ? field.checked : field.value;
					if (current !== initial) changed = true;
				});
				saveChangesButton.disabled = !changed;
			};

			this._editAccountResetButton = () => {
				snapshotValues();
				saveChangesButton.disabled = true;
			};

			form.addEventListener('input', checkChanges);
			form.addEventListener('change', checkChanges);
		}

		const messagesContainer = document.querySelector('.edit-account-messages')

		// Function to display messages
		const showMessage = (messages, isSuccess) => {
			if (!messagesContainer) return

			// Clear previous messages
			messagesContainer.innerHTML = ''

			// Handle single message or array of messages
			const messageArray = Array.isArray(messages) ? messages : [messages]

			// Create message HTML
			const messageHTML = messageArray.map(msg => `<div>${msg}</div>`).join('')
			messagesContainer.innerHTML = messageHTML

			// Set colors based on success/error
			if (isSuccess) {
				messagesContainer.style.backgroundColor = '#d4edda'
				messagesContainer.style.color = '#155724'
				messagesContainer.style.borderLeft = '4px solid #28a745'
			} else {
				messagesContainer.style.backgroundColor = '#f8d7da'
				messagesContainer.style.color = '#721c24'
				messagesContainer.style.borderLeft = '4px solid #dc3545'
			}

			// Show the container
			messagesContainer.style.display = 'block'

			// Scroll to messages
			messagesContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

			// Auto-hide success messages after 5 seconds
			if (isSuccess) {
				setTimeout(() => {
					messagesContainer.style.display = 'none'
				}, 5000)
			}
		}

		saveChangesButton.addEventListener('click', (e, button) => {
			e.preventDefault()
			const form = document.querySelector('.edit-account-form')
			if (!form) return;

			if (messagesContainer) {
				messagesContainer.style.display = 'none'
			}

			const requiredFields = form.querySelectorAll('input[required], select[required], textarea[required]');
			const errors = [];

			const humanizeFieldName = (name) => {
				let clean = name.replace(/^cf\[([^\]]+)\]$/, '$1');
				clean = clean.replace(/^scf_/, '').replace(/^billing_/, '').replace(/^shipping_/, '');
				return clean.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
			};

			requiredFields.forEach(field => {
				field.classList.remove('error');
				if (!field.value || !field.value.trim()) {
					field.classList.add('error');
					const label = field.closest('.form-group')?.querySelector('.levcharity_field_label')?.textContent?.replace(/\*/g, '').trim()
						|| field.placeholder?.replace(/\*/g, '').trim()
						|| humanizeFieldName(field.name);
					errors.push(label);
				}
			});

			if (errors.length) {
				showMessage(errors.map(label => `${label} is required.`), false);
				const scrollContainer = document.querySelector('.levcharity_my_account_content');
				if (scrollContainer) {
					scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
				}
				return;
			}

			const formData = new FormData(form)
			const formDataObject = {}

			// Convert FormData to plain object
			formData.forEach((value, key) => {
				formDataObject[key] = value
			})

			// Add action and nonce
			if (!form.dataset?.action || !form.dataset?.token) {
				showMessage('Form configuration is missing.', false);
				return;
			}
			formDataObject.action = form.dataset.action
			formDataObject.levcharity_my_account_edit_nonce = form.dataset.token

			if (formDataObject.billing_email) formDataObject.email = formDataObject.billing_email;
			if (formDataObject.billing_first_name) formDataObject.first_name = formDataObject.billing_first_name;
			if (formDataObject.billing_last_name) formDataObject.last_name = formDataObject.billing_last_name;

			Helper.request(admin_ajax[0], formDataObject)
				.then((response) => {
					console.log(response);
					if (response.success) {
						const messages = response.messages || response.message || 'Changes saved successfully!'
						showMessage(messages, true)
						if (this._editAccountResetButton) this._editAccountResetButton();

						// Redirect if needed
						if (response.redirect_url) {
							setTimeout(() => {
								window.location.href = response.redirect_url
							}, 2000)
						}
					} else {
						// Show error message(s)
						const messages = response.messages || response.message || 'An error occurred. Please try again.'
						showMessage(messages, false)
					}
				})
				.catch((error) => {
					console.error(error)
					const messages = error.messages || error.message || 'An error occurred. Please try again.'
					showMessage(messages, false)
				})

		})
	}



}
